// grading/dimensions.ts - skill dimensions + error codes (ENGINE_DESIGN.md §7.4).
//
// Every rule/heuristic failure maps to an ERROR CODE, and every code maps to a skill DIMENSION. The
// read axis already homes its codes in schema/read.ts (READ_ERROR_DIMENSION); this is the parallel
// HAND-axis map. Codes stay free-form strings in the record schemas (records.ts errorCodes[]) - this
// module is the closing table for the hand-axis subset, the same posture read.ts takes for its subset.
// It lives in the engine (not schema) because it is grading logic that S8 owns; the record schema's
// `errorCodes: string[]` / `skillScores: Record<string,number>` intentionally stay open.
import { handClassRank, } from '@postflop/schema';
/** The hand-axis skill dimensions (spec §11 / §7.4). snake_case to match the existing dimension keys
 *  (`value_extraction`) and the read-axis dimensions (`table_reading`, `player_profiling`). Opponent
 *  profiling reuses the read-axis `player_profiling` key so the two axes share one dimension there. */
export const HAND_DIMENSIONS = [
    'preflop_discipline',
    'bluff_selection',
    'value_extraction',
    'fold_discipline',
    'board_awareness', // board texture / position awareness
    'bet_sizing',
    'icm_awareness', // tournament / ICM awareness
    'player_profiling', // opponent profiling (shared with the read axis)
];
/** Hand-axis error code → dimension (ENGINE_DESIGN.md §7.4 table, plus the §9 progress-engine codes).
 *  A plain map (not a closed `Record<Code,…>`) because the code keyspace is intentionally open - new
 *  detectors add rows here. `codeDimension` falls back safely for an unmapped code. */
export const HAND_ERROR_DIMENSION = {
    // preflop discipline
    OPENED_OUT_OF_RANGE: 'preflop_discipline',
    CALLED_3BET_OOP_TRASH: 'preflop_discipline',
    BB_OVERFOLD_VS_BTN: 'preflop_discipline',
    // bluff selection
    BLUFFED_STATION: 'bluff_selection',
    MULTIWAY_BLUFF: 'bluff_selection',
    // value extraction
    MISSED_THIN_VALUE: 'value_extraction',
    // fold discipline
    PAID_OFF_ROCK: 'fold_discipline',
    HERO_CALL_NO_BLUFFS: 'fold_discipline',
    // board texture / position awareness
    OVERVALUED_ONE_PAIR_OOP: 'board_awareness',
    // bet sizing
    SIZE_TOO_SMALL_VS_INELASTIC: 'bet_sizing',
    SIZE_TOO_BIG: 'bet_sizing',
    ARITH_OFF: 'bet_sizing',
    // tournament / ICM awareness
    ICM_CALL_OFF: 'icm_awareness',
    MISSED_BUBBLE_PRESSURE: 'icm_awareness',
    // opponent profiling
    IGNORED_CONFIRMED_READ: 'player_profiling',
};
/** The dimension an error code attributes to; unmapped codes fall back to `board_awareness` (the
 *  catch-all "general postflop judgement" bucket) rather than throwing, so a new code never crashes. */
export const codeDimension = (code) => HAND_ERROR_DIMENSION[code] ?? 'board_awareness';
/** Which seed exploit rule, when VIOLATED (user acted against the recommended line it drove), attributes
 *  which error code (ENGINE_DESIGN.md §7.4: "MISSED_THIN_VALUE (R1 violated)", etc.). */
export const RULE_VIOLATION_CODE = {
    OVERCALLER_THIN_VALUE: 'MISSED_THIN_VALUE', // R1: didn't take the thin value vs a station.
    OVERCALLER_NO_BLUFF: 'BLUFFED_STATION', // R2: bluffed a station anyway.
    OVERCALLER_ISO_LIMPER: 'MISSED_THIN_VALUE',
    OVERCALLER_NO_SLOWPLAY: 'MISSED_THIN_VALUE',
    ROCK_RESPECT_AGGRESSION: 'PAID_OFF_ROCK', // R3: paid off a rock's aggression.
    TAG_RIVER_RESPECT: 'PAID_OFF_ROCK',
    ICM_PRESSURE_MEDIUM_STACK: 'ICM_CALL_OFF', // R5: called off under ICM pressure.
    ICM_COVERED_TIGHTEN: 'ICM_CALL_OFF',
    ICM_SHORT_JAM_DONT_CALL: 'ICM_CALL_OFF',
    ICM_CALL_OFF_PREMIUM_ONLY: 'ICM_CALL_OFF',
    SCARED_STACK_BIG_PRESSURE: 'MISSED_BUBBLE_PRESSURE',
};
/** Made-hand rank below which a bet/raise with no rule sanction reads as a bluff (air/ace-high/draw). */
const BLUFF_STRENGTH_CEIL = handClassRank('weak_pair');
const isAggressive = (a) => a === 'bet' || a === 'raise' || a === 'jam';
/**
 * Derive the hand-axis error codes for a decision from the deviation between the user's action and the
 * engine recommendation. Two sources (§7.4):
 *   1. Rule violations - a fired seed rule whose recommended line the user contradicted (the strongest,
 *      data-driven signal: the rule literally said what to do and the user did otherwise).
 *   2. Generic detectors - patterns that are leaks regardless of which rule fired (bluffing a sticky
 *      villain; raising one pair OOP into aggression).
 * Arithmetic (`ARITH_OFF`) is appended by the grade orchestrator, which owns the arithmetic verdict.
 * Returns a de-duplicated, stable-ordered list. Pure function of its inputs.
 */
export const deriveErrorCodes = (ctx, recommendation, userAction) => {
    const codes = [];
    const deviated = userAction.action !== recommendation.finalAction;
    // 1. rule violations - only when the user actually deviated from the recommended line.
    if (deviated) {
        for (const fired of recommendation.firedRules) {
            const code = RULE_VIOLATION_CODE[fired.id];
            if (code)
                codes.push(code);
        }
    }
    // 2. generic detectors.
    const r = handClassRank(ctx.hero.handClass);
    const villainSticky = ctx.villain.rangeShape.bluffPct <= 15 || ctx.villain.archetype.id === 'sticker' || ctx.villain.archetype.id === 'sheriff';
    // bluffed a sticky/station villain: aggression with a bluff-strength hand the baseline never bluffs,
    // and the recommendation did NOT sanction that aggression.
    if (isAggressive(userAction.action) &&
        r <= BLUFF_STRENGTH_CEIL &&
        ctx.hero.handClass !== 'strong_draw' &&
        !isAggressive(recommendation.finalAction) &&
        villainSticky) {
        if (!codes.includes('BLUFFED_STATION'))
            codes.push('BLUFFED_STATION');
    }
    // overvalued one pair OOP: raised/committed a one-pair hand out of position facing a bet when the
    // recommendation was to call or fold (bluff-catch, not bloat - §7.4 OVERVALUED_ONE_PAIR_OOP).
    const onePair = r >= handClassRank('weak_pair') && r <= handClassRank('overpair');
    if (ctx.positionClass === 'OOP' &&
        ctx.price.toCall > 0 &&
        onePair &&
        (userAction.action === 'raise' || userAction.action === 'jam') &&
        (recommendation.finalAction === 'call' || recommendation.finalAction === 'fold')) {
        if (!codes.includes('OVERVALUED_ONE_PAIR_OOP'))
            codes.push('OVERVALUED_ONE_PAIR_OOP');
    }
    return codes;
};
/**
 * Attribute per-dimension skill scores from the fired error codes (§7.4): each TOUCHED dimension scores
 * `10 − dimension-attributed loss`, bounded 1–10; untouched dimensions are ABSENT (never averaged in as
 * a neutral). The dimension-attributed loss is the decision's evLoss in bb (a 3-bb mistake → 7); on an
 * abstention (evLoss null) we fall back to the composite grade so the dimension still records the miss.
 */
export const attributeDimensions = (errorCodes, evLossBB, compositeGrade) => {
    const scores = {};
    const attributed = evLossBB === null ? compositeGrade : Math.max(1, Math.min(10, Math.round(10 - evLossBB)));
    for (const code of errorCodes) {
        const dim = codeDimension(code);
        // if multiple codes touch one dimension, keep the WORST (min) - the biggest leak in that skill.
        scores[dim] = scores[dim] === undefined ? attributed : Math.min(scores[dim], attributed);
    }
    return scores;
};
//# sourceMappingURL=dimensions.js.map